import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { Box, Button, Divider, IconButton, InputAdornment, MenuItem, useTheme } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';
import { ControlledCheckbox } from '@frontend/features/common/components/ControlledCheckbox';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import { FlightIntegrationDto, ICreateFlightIntegration, IUpdateFlightIntegration } from '@bandido/shared/dtos';
import { FlightIntegrationProviderEnum } from '@bandido/shared/constants';
import {useEffect, useState} from 'react';

interface FormProps {
  onClose: () => void;
  integrationToEdit?: FlightIntegrationDto;
}

interface CreateFlightIntegrationFormInput {
  provider: FlightIntegrationProviderEnum;
  allowSeeByOthers: boolean;
  username?: string;
  password?: string;
}

export const FormCreateOrEditFlightIntegration = ({ onClose, integrationToEdit }: FormProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();

  const [showPassword, setShowPassword] = useState(false);

  const schema = yup.object().shape({
    provider: yup
      .mixed<FlightIntegrationProviderEnum>()
      .oneOf(Object.values(FlightIntegrationProviderEnum))
      .required(t('common.form.required'))
      .default(undefined),
    allowSeeByOthers: yup.boolean().required(),
    username: yup.string().when('provider', {
      is: (val: FlightIntegrationProviderEnum) => val === FlightIntegrationProviderEnum.STARLINGS,
      then: (schema) => schema.required(t('common.form.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    password: yup.string().when('provider', {
      is: (val: FlightIntegrationProviderEnum) => val === FlightIntegrationProviderEnum.STARLINGS,
      then: (schema) => schema.required(t('common.form.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const methods = useForm<CreateFlightIntegrationFormInput>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      provider: undefined,
      allowSeeByOthers: false,
      username: '',
      password: '',
    }
  });

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isValid, isSubmitting, errors },
  } = methods;

  useEffect(() => {
    if (integrationToEdit) {
      reset({
        provider: integrationToEdit.provider,
        allowSeeByOthers: integrationToEdit.allowSeeByOthers,
        username: integrationToEdit.rawCredentials?.username ?? '',
        password: integrationToEdit.rawCredentials?.password ?? '',
      });
    }
  }, [integrationToEdit, reset]);

  const watchProvider = watch('provider');

  const onSubmit = async (data: CreateFlightIntegrationFormInput) => {
    const { username, password, provider, allowSeeByOthers } = data;

    const rawCredentials = provider === FlightIntegrationProviderEnum.STARLINGS ? { username, password } : {};

    try {
      if (integrationToEdit) {
        const payload: IUpdateFlightIntegration = {
          rawCredentials,
          allowSeeByOthers,
        };

        await client.put(`/flight-integrations/${integrationToEdit.id}`, payload);
        addToast({
          message: t('flightIntegrations.edit.updatedSuccess'),
          color: 'success',
        });
      } else {
        const payload: ICreateFlightIntegration = {
          provider,
          rawCredentials,
          allowSeeByOthers,
        };

        await client.post('/flight-integrations', payload);
        addToast({
          message: t('flightIntegrations.create.createdSuccess'),
          color: 'success',
        });
      }
    } catch (error) {
      addToast({
        message: t(
          integrationToEdit ? 'flightIntegrations.edit.updatedError' : 'flightIntegrations.create.createdError',
        ),
        color: 'error',
      });
      console.error(error);
    } finally {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.getAllFlightIntegrations,
      });
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box gridColumn="span 12">
          <ControlledSelect
            variant="outlined"
            name="provider"
            required
            control={control}
            placeholder={t('common.form.select')}
            label={t('flightIntegrations.form.provider')}
            size="small"
            fullWidth
            error={!!errors.provider}
            helperText={errors.provider?.message}
          >
            {Object.values(FlightIntegrationProviderEnum).map((value) => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </ControlledSelect>
        </Box>

        {watchProvider === FlightIntegrationProviderEnum.STARLINGS && (
          <>
            <Box gridColumn="span 12" mt={2}>
              <ControlledTextField
                name="username"
                control={control}
                label={t('flightIntegrations.form.username')}
                variant="outlined"
                size="small"
                required
                fullWidth
                error={!!errors.username}
                helperText={errors.username?.message}
              />
            </Box>
            <Box gridColumn="span 12" mt={2}>
              <ControlledTextField
                name="password"
                control={control}
                label={t('flightIntegrations.form.password')}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                size="small"
                required
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </>
        )}

        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(1),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledCheckbox
              name="allowSeeByOthers"
              control={control}
              label={t('flightIntegrations.form.allowSeeByOtherClients')}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" type="submit" size="medium" disabled={!isValid || isSubmitting}>
              {t('common.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
